import { Group, TextInput, Button, Text } from '@mantine/core';
import { Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { QuickFilterConfig, FilterFieldConfig, ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { SortState } from '../../../utils/sortTasks';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { DataLibraryFilterOptions } from '../../../types';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

interface DataLibraryToolbarProps {
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;

  // Filters
  filterOptions: DataLibraryFilterOptions;
  activeQuickFilters: string[];
  onQuickFilterToggle: (key: string) => void;
  activeFilters: ActiveFilterRow[];
  onFilterAdd: (key: string) => void;
  onFilterChange: (key: string, values: string[]) => void;
  onFilterRemove: (key: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;

  // Sort
  sortConfig: SortConfig | null;
  onSortChange: (sort: SortState | null) => void;

  // Group
  activeGroup: GroupState | null;
  /** Filtered row total when it is too large to group */
  groupLimitTotal?: number | null;
  onGroupChange: (group: GroupState | null) => void;

  // Columns
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;

  // Meta
  totalCount: number;
  selectedCohortIds: string[];
}

const QUICK_FILTERS: QuickFilterConfig[] = [
  { key: 'flagged', label: 'Flagged' },
  { key: 'absences', label: 'Has Absences' },
];

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'status', label: 'Status' },
  { value: 'cohort', label: 'Cohort' },
  { value: 'group', label: 'Group' },
  { value: 'coach', label: 'Coach' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'role', label: 'Role' },
  { value: 'department', label: 'Department' },
  { value: 'location', label: 'Location' },
  { value: 'enrolled_at', label: 'Enrolled Date' },
  { value: 'flag_severity', label: 'Flag Severity' },
  { value: 'name', label: 'Full Name' },
];

export function DataLibraryToolbar({
  searchValue,
  onSearchChange,
  filterOptions,
  activeQuickFilters,
  onQuickFilterToggle,
  activeFilters,
  onFilterAdd,
  onFilterChange,
  onFilterRemove,
  onClearAll,
  hasActiveFilters,
  sortConfig,
  onSortChange,
  activeGroup,
  groupLimitTotal,
  onGroupChange,
  columns,
  onColumnsChange,
  totalCount,
  selectedCohortIds,
}: DataLibraryToolbarProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);
  const filterFields: FilterFieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: 'Status',
        type: 'select' as const,
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'active', label: 'Active' },
          { value: 'withdrawn', label: 'Withdrawn' },
        ],
      },
      {
        key: 'group',
        label: 'Group',
        type: 'multiselect' as const,
        options: filterOptions.groups,
      },
      {
        key: 'coach',
        label: 'Coach',
        type: 'select' as const,
        options: filterOptions.coaches,
      },
      {
        key: 'department',
        label: 'Department',
        type: 'select' as const,
        options: filterOptions.departments.map(d => ({ value: d, label: d })),
      },
      {
        key: 'location',
        label: 'Location',
        type: 'select' as const,
        options: filterOptions.locations.map(l => ({ value: l, label: l })),
      },
      {
        key: 'midpoint',
        label: 'Midpoint Completed',
        type: 'select' as const,
        options: [
          { value: 'true', label: 'Completed' },
          { value: 'false', label: 'Not Completed' },
        ],
      },
      {
        key: 'endpoint',
        label: 'Endpoint Completed',
        type: 'select' as const,
        options: [
          { value: 'true', label: 'Completed' },
          { value: 'false', label: 'Not Completed' },
        ],
      },
    ],
    [filterOptions],
  );

  // Convert SortConfig to SortState for SortPanel
  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  return (
    <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
      <Group gap="xs" wrap="wrap">
        <TextInput
          placeholder="Search participants..."
          size="sm"
          leftSection={<Search size={14} />}
          rightSection={
            searchValue ? (
              <X
                size={14}
                style={{ cursor: 'pointer' }}
                onClick={() => onSearchChange('')}
              />
            ) : null
          }
          value={searchValue}
          onChange={e => onSearchChange(e.currentTarget.value)}
          w={220}
        />

        <FilterPanel
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          onClearAll={onClearAll}
          size="sm"
        />

        <SortPanel
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          defaultSort={{ field: 'first_name', direction: 'asc' }}
          onSortChange={onSortChange}
          size="sm"
        />

        <GroupPanel
          groupFields={groupFields}
          activeGroup={activeGroup}
          limitTotal={groupLimitTotal}
          onGroupChange={onGroupChange}
          size="sm"
        />

        <SavedViewsMenu
          columns={columns}
          onColumnsChange={onColumnsChange}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          onSortChange={onSortChange}
          groupFields={groupFields}
          activeGroup={activeGroup}
          onGroupChange={onGroupChange}
          size="sm"
        />

        {hasActiveFilters && (
          <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(onClearAll)}>
            Clear
          </Button>
        )}
      </Group>

      <Group gap="xs">
        <Text size="sm" c="dimmed">
          {totalCount} participant{totalCount !== 1 ? 's' : ''}
        </Text>
        <ColumnSettingsPopover columns={columns} onColumnsChange={onColumnsChange} zIndex={1000} />
        <Button
          variant="outline"
          color="gray"
          bg="white"
          size="sm"
          style={{ borderColor: '#E8E6E3' }}
          leftSection={<Download size={14} />}
          onClick={() => setExportOpen(true)}
        >
          Export
        </Button>
      </Group>

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="participants"
        contextType={selectedCohortIds.length === 1 ? 'cohort' : 'global'}
        contextId={selectedCohortIds.length === 1 ? selectedCohortIds[0] : undefined}
        scopeContext={{ cohortIds: selectedCohortIds }}
        filterContext={{ groupIds: activeFilters.find(f => f.key === 'group')?.values }}
        {...exportSummaryProps(
          splitScreenFilters({ search: searchValue, quickFilters: QUICK_FILTERS, activeQuickFilters, fields: filterFields, rows: activeFilters }, ['group']),
        )}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
      />
    </Group>
  );
}
